import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Send, CheckCircle2, Crown, Shield, Loader2, Lightbulb, Heart, ArrowRight } from 'lucide-react';
import { submitContact } from '../lib/api';
import { usePageTitle } from '../hooks/usePageTitle';

export default function Abonnement() {
  usePageTitle('Abonnement Pro');
  const { user } = useAuth();
  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    etablissement: '',
    academie: '',
    email: user?.email || '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.etablissement || !form.academie || !form.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success('Demande envoyée avec succès !');
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in py-4" data-testid="abonnement-success">
        <Card className="bg-card border-border border-emerald-500/30">
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
            <h2 className="text-2xl font-black mb-2">Demande envoyée !</h2>
            <p className="text-muted-foreground mb-6">
              Nous avons bien reçu votre demande d'abonnement. Notre équipe vous recontactera dans les plus brefs délais pour finaliser votre accès Pro.
            </p>
            <Button onClick={() => window.history.back()} variant="secondary" data-testid="abonnement-back-btn">
              Retour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-10 animate-fade-in py-4" data-testid="abonnement-page">

      {/* Header */}
      <div className="space-y-3">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="abonnement-title">Passer à Pro</h1>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Débloquez toutes les fonctionnalités de QCMAntiTriche Pro et gagnez un temps précieux dans la création, l'impression et la correction de vos QCM.
        </p>
        <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-2xl">
          L'abonnement Pro est actuellement activé sur demande. Le paiement en ligne n'est pas encore disponible directement sur la plateforme : notre équipe traite donc chaque demande manuellement pour finaliser votre accès.
        </p>
      </div>

      {/* Pricing Card */}
      <Card className="bg-card border-border border-primary/30 overflow-hidden" data-testid="abonnement-pricing-card">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-1" />
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="font-black text-xl">Abonnement Pro</span>
              </div>
              <p className="text-muted-foreground text-sm">Accès complet à toutes les fonctionnalités avancées</p>
            </div>
            <div className="text-left sm:text-right shrink-0">
              <span className="text-4xl font-black" data-testid="abonnement-price">9,90€</span>
              <span className="text-muted-foreground text-sm">/mois</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              'QCM illimités',
              'Génération PDF illimitée',
              'Scanner OCR illimité',
              'Génération IA de questions',
              'Export CSV et statistiques avancées',
              'Support prioritaire'
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Why Paid */}
      <section className="space-y-4" data-testid="abonnement-why-paid">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
            <Lightbulb className="w-4 h-4 text-blue-400" />
          </div>
          <h2 className="text-lg font-bold">Pourquoi QCMAntiTriche est payant</h2>
        </div>
        <div className="space-y-3 text-sm text-muted-foreground leading-relaxed pl-[42px]">
          <p>
            QCMAntiTriche repose sur une infrastructure technique réelle, avec des coûts concrets : utilisation de l'intelligence artificielle, hébergement, maintenance, sécurité, développement et amélioration continue de la plateforme.
          </p>
          <p>
            L'abonnement permet de financer un service utile, fiable et durable pour les enseignants. Il permet non seulement de faire fonctionner la plateforme au quotidien, mais aussi de développer de nouvelles fonctionnalités à partir des besoins exprimés sur le terrain.
          </p>
          <p>
            QCMAntiTriche est un projet indépendant. Son évolution dépend directement du soutien de ses utilisateurs. Chaque abonnement contribue donc à maintenir un outil qui fait gagner du temps, réduit la charge de correction et répond à des usages pédagogiques très concrets.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="space-y-4" data-testid="abonnement-founder-story">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <Heart className="w-4 h-4 text-amber-400" />
          </div>
          <h2 className="text-lg font-bold">Pourquoi j'ai créé QCMAntiTriche</h2>
        </div>
        <div className="space-y-3 text-sm text-muted-foreground leading-relaxed pl-[42px]">
          <p>
            En observant les difficultés rencontrées par les enseignants face à la triche, à la correction chronophage et au manque d'outils vraiment adaptés aux évaluations sur papier, un besoin m'est apparu clairement : proposer une solution simple, concrète et efficace.
          </p>
          <p>
            QCMAntiTriche a été conçu pour répondre à cette réalité de terrain. L'objectif n'était pas de créer un outil de plus, mais une plateforme réellement utile au quotidien : générer des QCM personnalisés, les imprimer facilement, limiter les échanges entre élèves et réduire le temps passé à corriger.
          </p>
        </div>
      </section>

      {/* Manual Process */}
      <section className="space-y-4" data-testid="abonnement-activation">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <ArrowRight className="w-4 h-4 text-emerald-400" />
          </div>
          <h2 className="text-lg font-bold">Comment fonctionne l'activation</h2>
        </div>
        <div className="space-y-2 text-sm text-muted-foreground leading-relaxed pl-[42px]">
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <p>Remplissez le formulaire ci-dessous pour demander votre accès à QCMAntiTriche Pro.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <p>Notre équipe vous recontactera pour finaliser l'inscription et répondre à vos éventuelles questions.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <p>Cette étape est actuellement nécessaire car l'activation des abonnements est encore gérée manuellement.</p>
          </div>
        </div>
      </section>

      {/* Subscription Form */}
      <Card className="bg-card border-border" data-testid="abonnement-form-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Demande d'abonnement
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Remplissez ce formulaire pour demander votre accès à QCMAntiTriche Pro. Notre équipe vous recontactera pour finaliser l'inscription et répondre à vos éventuelles questions.
          </p>
          <p className="text-xs text-muted-foreground/70">
            Cette étape est actuellement nécessaire car l'activation des abonnements est encore gérée manuellement.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="abonnement-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  data-testid="abonnement-prenom"
                  value={form.prenom}
                  onChange={(e) => setForm(p => ({ ...p, prenom: e.target.value }))}
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  data-testid="abonnement-nom"
                  value={form.nom}
                  onChange={(e) => setForm(p => ({ ...p, nom: e.target.value }))}
                  placeholder="Votre nom"
                />
              </div>
            </div>
            <div>
              <Label>Établissement scolaire *</Label>
              <Input
                data-testid="abonnement-etablissement"
                value={form.etablissement}
                onChange={(e) => setForm(p => ({ ...p, etablissement: e.target.value }))}
                placeholder="ex: Collège Victor Hugo, Lyon"
              />
            </div>
            <div>
              <Label>Académie *</Label>
              <Input
                data-testid="abonnement-academie"
                value={form.academie}
                onChange={(e) => setForm(p => ({ ...p, academie: e.target.value }))}
                placeholder="ex: Académie de Lyon"
              />
            </div>
            <div>
              <Label>Adresse email *</Label>
              <Input
                data-testid="abonnement-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="vous@etablissement.fr"
              />
            </div>
            <div>
              <Label>Votre message</Label>
              <Textarea
                data-testid="abonnement-message"
                value={form.message}
                onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                placeholder="Précisez votre besoin si vous le souhaitez : niveau(x) enseigné(s), nombre de classes, usage envisagé, ou toute question concernant l'abonnement Pro."
                className="min-h-[120px]"
              />
            </div>
            <Button
              type="submit"
              data-testid="abonnement-submit-btn"
              disabled={sending}
              className="w-full bg-primary text-primary-foreground hover:bg-blue-600 h-11"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
              ) : (
                <><Crown className="w-4 h-4 mr-2" /> Demander mon accès Pro</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center pb-4" data-testid="abonnement-privacy">
        <Shield className="w-3 h-3 inline mr-1" />
        Vos données sont traitées uniquement pour répondre à votre demande d'abonnement et vous recontacter. Elles ne sont ni cédées ni utilisées à d'autres fins.
      </p>
    </div>
  );
}
